import type { EvmFullFillCreateOrder, EvmFullFillOrderList } from '~/service/api/types';
import type { EvmFullFillOrderType } from '~/service/EVM/types';
import EvmProvider, { type EvmProviderInterface } from '~/service/EVM/provider';
import type { OrderComparisonResult, OrderSnapshot, ValidateOrderWithUuid } from '~/service/utils/validator/order/types';

export type OrderValidatorInterface = EvmProviderInterface;
/**
 * OrderValidator is a service responsible for validating orders
 * by comparing the current known state (e.g. from cart) against
 * the latest data returned from the backend (bestOrders API).
 *
 * It extends the base EvmProvider and encapsulates domain logic
 * for determining whether an order has changed, expired, or remains valid.
 */
export class OrderValidator extends EvmProvider {
    /**
     * Constructs the OrderValidator service.
     *
     * @param config - Provider/network configuration
     * @param eventEmitter
     * @param chainMap
     */
    constructor({
        config,
        eventEmitter,
        chainMap
    }: OrderValidatorInterface) {
        super({ config, eventEmitter, chainMap });
    }

    /**
     * Partitions the incoming list of orders into two groups:
     * - `withError`: orders that contain an error field
     * - `withoutError`: valid orders grouped by protocol contract address
     *
     * @param data - List of orders returned from the backend
     * @param paymentValues - Corresponding user cart items for matching amounts
     * @returns An object with `withError` and `withoutError` grouped orders
     */
    public partitionOrders(
        data: EvmFullFillCreateOrder[],
        paymentValues: AppCartItemType[]
    ): {
            withError: EvmFullFillCreateOrder[];
            withoutError: Record<string, EvmFullFillOrderType[]>;
        } {
        return data.reduce(
            (acc, item, index) => {
                if (item.error) {
                    acc.withError.push(item);
                }
                else {
                    const { protocolAddress } = item.listing.order;
                    if (!acc.withoutError[protocolAddress]) {
                        acc.withoutError[protocolAddress] = [];
                    }
                    acc.withoutError[protocolAddress].push({
                        ...item,
                        amount: paymentValues[index]?.amount
                    });
                }
                return acc;
            },
            {
                withError: [] as EvmFullFillCreateOrder[],
                withoutError: {} as Record<EvmFullFillCreateOrder['listing']['order']['protocolAddress'], EvmFullFillOrderType[]>
            }
        );
    }

    /**
     * Validates all cart items against the latest best order data.
     *
     * Iterates through each item, compares it with the best order data, and
     * returns a list of changes:
     * - `expired`: if the order is no longer available
     * - `return_best`: if the price or quantity changed
     * - skips unchanged (idle) items
     *
     * @param items - Cart items to validate
     * @param orderList - Flattened list of best orders to match against
     * @param type - (Optional) transaction context
     * @returns List of validation results with `uuid` and change status
     */
    public async validateBestOrder({ items, orderList, type = 'purchase:cart' }: {
        items: AppCartItemType[];
        orderList: EvmFullFillOrderList;
        type?: AppTransaction['type'];
    }) {
        const result: ValidateOrderWithUuid[] = [];

        for (const item of items) {
            const bestOrderListing = orderList.find(order => order.uuid === item.uuid)?.listing;

            const response = this.validateOrderChanges({
                previous: item && item.price
                    ? {
                            uuid: item.uuid,
                            maxAmount: item.maxAmount,
                            name: item.name,
                            price: item.price
                        }
                    : undefined,
                best: bestOrderListing && bestOrderListing.price
                    ? {
                            uuid: bestOrderListing.nftInfo.uuid,
                            maxAmount: bestOrderListing.tokensRemaining,
                            name: bestOrderListing.nftInfo.name,
                            price: bestOrderListing.price
                        }
                    : undefined
            });

            if (response.status === 'idle') {
                continue;
            }

            if (response.status === 'expired') {
                result.push({
                    ...response,
                    uuid: item.uuid
                });
            }

            else if (response?.order?.price) {
                result.push({
                    ...response,
                    uuid: item.uuid
                });
            }
        }

        return result;
    }

    /**
     * Compares the previous state of the order (from cart) with the current best order (from API).
     *
     * - If no previous order: returns 'idle'
     * - If best order is missing: returns 'expired'
     * - If price and max amount are unchanged: returns 'idle'
     * - Otherwise: returns 'return_best' with updated details
     *
     * @param previous - Snapshot of the previous order (from the cart)
     * @param best - Latest order from the backend
     * @returns A result indicating whether the order is unchanged, expired, or updated
     */
    private validateOrderChanges({
        previous,
                              best
    }: {
        previous?: OrderSnapshot;
        best?: OrderSnapshot;
    }): OrderComparisonResult {
        if (!previous) return { status: 'idle' };

        if (!best) return {
            status: 'expired',
            message: `Order for token ${previous.name} was expired`
        };

        const priceEqual = previous.price?.amount === best.price?.amount;
        const isMaxAmountNotDecreased = previous.maxAmount <= best.maxAmount;

        if (priceEqual && isMaxAmountNotDecreased) return { status: 'idle' };

        return {
            status: 'return_best',
            order: best,
            message: `Token ${previous.name} now costs ${best.price?.amount}, available: ${best.maxAmount}`
        };
    }
}
